import {
  defineComponent,
  ref,
  useModel,
  computed,
  type PropType,
  onMounted,
  watch,
  nextTick,
} from 'vue'
import type { LanguageData } from './types'
import CountrySelector from '../country-selector/CountrySelector'
import { InputNumber } from 'ant-design-vue'
import { useInjectFormItemContext } from 'ant-design-vue/es/form/FormItemContext'
import { useWindowSize } from '@vueuse/core'
import './HPhoneInput.css'

export default defineComponent({
  name: 'HPhoneInput',
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: '',
    },
    buttonText: {
      type: String,
      default: 'Send message',
    },
    locale: {
      type: String,
      default: 'en',
    },
    inputClass: {
      type: String,
      default: 'text-ink-900',
    },
    overlayClass: {
      type: String,
      default: 'bg-ground-200',
    },
    modelValue: {
      type: String,
      default: undefined,
    },
    value: {
      type: String,
      default: undefined,
    },
    phoneCode: {
      type: String,
      default: undefined,
    },
    showError: {
      type: Boolean,
      default: true,
    },
    theme: {
      type: String,
      default: 'default',
    },
    normalizeOnBlur: {
      type: Boolean,
      default: true,
    },
    size: {
      type: String as PropType<'xs' | 'sm' | 'md' | 'lg' | 'xl'>,
      default: 'md',
      validator: (v): v is 'xs' | 'sm' | 'md' | 'lg' | 'xl' =>
        ['xs', 'sm', 'md', 'lg', 'xl'].includes(v as any),
    },
    spaceSeparated: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue', 'update:value', 'update:phoneCode', 'change', 'blur'],
  setup(props, { slots, emit }) {
    const formItemContext = useInjectFormItemContext()
    const modelValue = useModel(props, 'modelValue')
    const value = useModel(props, 'value')

    const inputValue = ref('')

    const phoneCode = useModel(props, 'phoneCode')
    const isInitializing = ref(true)
    const isSyncing = ref(false)
    let rafId: number | null = null
    const localeData = ref<LanguageData | null>(null)
    const errorMessage = ref('')

    const { width } = useWindowSize()
    const isMobile = computed(() => width.value < 768)

    const handleNumberChange = (val: string | number | null) => {
      const s = String(val ?? '')
      inputValue.value = s
      const full = syncCompositeValue()
      if (!isInitializing.value) {
        notifyFormChange(full)
      }
    }

    const handlePhoneCodeChange = (phoneCodeString: string) => {
      phoneCode.value = phoneCodeString
      const full = syncCompositeValue()
      if (!isInitializing.value) {
        notifyFormChange(full)
      }
    }

    function parseE164AndSyncFromOutside(full?: string) {
      const raw0 = String(full ?? '')
      const raw = raw0.replace(/[\u00A0\u202F\u2007]/g, ' ').trim()
      if (!raw) {
        return
      }

      const spaced = /^\+\s*([1-9]\d{0,14})\s+(.+)$/.exec(raw)
      if (spaced) {
        const [, code, localRaw] = spaced
        const num = (localRaw || '').replace(/\D/g, '')
        if (code && phoneCode.value !== code) phoneCode.value = code
        if (typeof num === 'string' && inputValue.value !== num) inputValue.value = num
        syncCompositeValue()
        return
      }

      const compact = /^\+\s*([0-9]{1,4})([0-9]{3,})$/.exec(raw)
      if (compact) {
        const [, code, numRaw] = compact
        const num = (numRaw || '').replace(/\D/g, '')
        if (code && phoneCode.value !== code) phoneCode.value = code
        if (typeof num === 'string' && inputValue.value !== num) inputValue.value = num
        syncCompositeValue()
        return
      }

      return
    }

    function notifyFormChange(nextVal: string) {
      if (rafId) cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(() => {
        formItemContext?.onFieldChange?.()
        emit('change', nextVal)
        rafId = null
      })
    }

    onMounted(() => {
      if (props.modelValue || props.value) {
        parseE164AndSyncFromOutside(props.modelValue ?? props.value)
      } else {
        syncCompositeValue()
      }
      nextTick(() => {
        isInitializing.value = false
      })
    })

    watch(modelValue, (nv) => {
      if (isSyncing.value) return
      parseE164AndSyncFromOutside(nv)
    })

    watch(value, (nv) => {
      if (isSyncing.value) return
      parseE164AndSyncFromOutside(nv)
    })

    const wrapperClass = computed(() => {
      const sizeMap = {
        'px-1 py-2 rounded-sm text-xs h-[28px]': props.size === 'xs',
        'px-[5px] py-2 rounded-sm text-xs h-[30px]': props.size === 'sm',
        'px-2 py-[10px] rounded-lg text-sm h-[38px]': props.size === 'md',
        'px-2 py-3 rounded-[10px] text-sm h-[42px]': props.size === 'lg',
        'px-[13px] py-3 rounded-[10px] text-sm h-[48px]': props.size === 'xl',
      }

      const borderClass = 'border border-rim-300 hover:border-ink-400 focus:border-blue-primary'
      const bgClass = props.disabled || props.readonly ? 'bg-ground-50' : 'bg-transparent'
      const disabledClass =
        props.disabled || props.readonly ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'

      const flexDirection = isMobile.value
        ? 'flex-col justify-center items-start !h-auto'
        : 'flex-row items-center'

      return [sizeMap, borderClass, bgClass, disabledClass, flexDirection]
    })

    function syncCompositeValue(): string {
      const code = String(phoneCode.value ?? '').replace(/\D/g, '')
      const num = String(inputValue.value ?? '').replace(/\D/g, '')

      if (!code || !num) {
        return ''
      }

      const full = props.spaceSeparated ? `+${code} ${num}` : `+${code}${num}`

      isSyncing.value = true
      try {
        if (modelValue.value !== full) modelValue.value = full
        if (value.value !== full) value.value = full
      } finally {
        nextTick(() => {
          isSyncing.value = false
        })
      }

      return full
    }

    const defaultRender = ({}) => (
      <div
        class={[
          'w-full flex flex-col',
          'phone-input',
          `phone-input-${props.theme}`,
          `phone-input-${props.size}`,
          { 'phone-input-disabled': props.disabled },
        ]}
      >
        <div class={['inner flex items-center gap-2', wrapperClass.value]}>
          <CountrySelector
            class={[isMobile.value ? 'w-full' : 'w-fit']}
            phoneCode={phoneCode.value || ''}
            mode="phone"
            bordered={false}
            overlayClass={props.overlayClass}
            onUpdate:phoneCode={handlePhoneCodeChange}
          />
          <div class={['flex items-center flex-1 gap-2', isMobile.value && 'w-full flex-none']}>
            {phoneCode.value && (
              <div class={[props.inputClass, 'flex-none']}>{'+' + phoneCode.value}</div>
            )}
            <InputNumber
              v-model:value={inputValue.value}
              stringMode
              controls={false}
              precision={0}
              parser={(v) => (v ?? '').toString().replace(/\D/g, '')}
              formatter={(v) => (v ?? '').toString().replace(/\D/g, '')}
              disabled={props.disabled}
              placeholder={props.placeholder || localeData.value?.placeholder}
              onChange={handleNumberChange}
              onBlur={() => {
                formItemContext?.onFieldBlur?.()
                emit('blur')
              }}
              class={[
                props.inputClass,
                'no-ring',
                'flex-1',
                'px-2',
                '!border-none',
                'rounded-md',
                'outline-none',
                'disabled:bg-gray-100',
                'disabled:cursor-not-allowed',
                'bg-transparent',
              ]}
              style={'background-color: transparent !important;'}
            />
          </div>
        </div>
        {props.showError && errorMessage.value && (
          <div class={'text-error mt-1 px-4'}>{errorMessage.value}</div>
        )}
      </div>
    )

    return () => {
      const slotProps = {
        value: inputValue.value,
        locale: localeData.value,
        defaultRender,
      }
      if (slots.default) {
        return slots.default(slotProps)
      }
      return defaultRender(slotProps)
    }
  },
})
